import { Icon, PageContainer, SelectableCard, SelectableCardGroup } from "@/components/ui"
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling"
import { Calendar, Lightning } from "@/icons"
import { formatWindowOpening } from "@/lib/scheduling/format"
import { useAtom } from "jotai"
import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useCheckoutContext } from "../CheckoutContext"
import { StepFooter } from "../components/StepFooter"
import { StepHeader } from "../components/StepHeader"
import { useEnforceWizardEntry } from "../hooks/useEnforceWizardEntry"
import { applyOrderTiming, checkoutAtom } from "../state"

/**
 * First question of the wizard: immediate or scheduled. Only rendered when the
 * merchant takes BOTH modalities at this instant (D15) — otherwise the layout
 * applies the single modality silently and this step never exists.
 */
export function TimingStep() {
  const { addresses } = useCheckoutContext()
  const [state, setState] = useAtom(checkoutAtom)
  const scheduling = useStorefrontScheduling()
  const navigate = useNavigate()

  useEnforceWizardEntry("timing", addresses)

  useEffect(() => {
    if (scheduling.known && !scheduling.timingStepVisible) {
      navigate("/cart/checkout/service", { replace: true })
    }
  }, [scheduling.known, scheduling.timingStepVisible, navigate])

  const nextWindowAt = scheduling.schedulableChannels[0]?.targetWindow?.startsAt ?? null

  return (
    <>
      <StepHeader
        title="Quando você quer receber seu pedido?"
        description="Escolher agora ou agendar muda os horários e as formas de pagamento disponíveis."
      />

      <PageContainer as="section">
        <SelectableCardGroup
          label="Quando receber"
          value={state.orderTiming}
          onValueChange={(timing) =>
            setState((prev) => applyOrderTiming(prev, timing === "SCHEDULED" ? "SCHEDULED" : "INSTANT"))
          }
        >
          <SelectableCard
            value="INSTANT"
            icon={<Icon as={Lightning} size="xl" />}
            title="Agora"
            subtitle="A loja começa a preparar assim que confirmar o pedido."
            aria-label="Pedir agora"
            tone="secondary"
            density="sm"
            wrapTitle
          />
          <SelectableCard
            value="SCHEDULED"
            icon={<Icon as={Calendar} size="xl" />}
            title="Agendar"
            subtitle={
              nextWindowAt
                ? `Escolha um horário da janela que abre ${formatWindowOpening(nextWindowAt, new Date(), scheduling.timezone ?? undefined)}.`
                : "Escolha um dos horários que a loja oferece."
            }
            aria-label="Agendar o pedido"
            tone="secondary"
            density="sm"
            wrapTitle
          />
        </SelectableCardGroup>
      </PageContainer>

      <StepFooter showBack={false} continueLabel="Continuar" onContinue={() => navigate("/cart/checkout/service")} />
    </>
  )
}
